import { useState, useRef, useCallback, useEffect } from "react";

export interface AudioCaptureState {
  isRecording: boolean;
  audioLevel: number;
  hasPermission: boolean;
  permissionError: string | null;
  isInitialized: boolean;
}

export interface AudioCaptureCallbacks {
  onAudioData?: (audioData: string, sequence: number) => void;
  onError?: (error: string) => void;
}

export function useAudioCapture(callbacks: AudioCaptureCallbacks = {}) {
  const [state, setState] = useState<AudioCaptureState>({
    isRecording: false,
    audioLevel: 0,
    hasPermission: false,
    permissionError: null,
    isInitialized: false,
  });

  // Refs for audio processing
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelIntervalRef = useRef<number | null>(null);
  const audioSequenceRef = useRef<number>(0);
  const isRecordingRef = useRef<boolean>(false);

  // Initialize audio context and request microphone permission
  const initialize = useCallback(async () => {
    try {
      console.log("Audio initialize started");
      setState((prev) => ({ ...prev, permissionError: null }));

      // Request microphone access with optimal settings for speech
      console.log("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Force mono for consistency
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Additional constraints for better speech quality
          googEchoCancellation: true,
          googAutoGainControl: true,
          googNoiseSuppression: true,
          googHighpassFilter: true,
          googTypingNoiseDetection: true,
        } as any,
      });

      console.log("Microphone access granted, stream:", stream);
      mediaStreamRef.current = stream;

      // Create audio context without forcing sample rate to avoid conflicts
      console.log("Creating audio context...");
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Resume audio context if it's suspended (required by some browsers)
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      // Create audio processing nodes
      const source = audioContext.createMediaStreamSource(stream);

      // Create analyser for audio level visualization
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;

      // Create script processor for audio data extraction
      // Use a buffer size that works well with the actual sample rate
      const bufferSize = 4096; // Standard buffer size that works across different sample rates
      const processor = audioContext.createScriptProcessor(
        bufferSize,
        1, // Input channels (mono)
        1 // Output channels (mono)
      );
      processorRef.current = processor;

      // Connect the audio processing chain
      source.connect(analyser);
      source.connect(processor);
      processor.connect(audioContext.destination);

      // Set up audio data processing
      processor.onaudioprocess = (event) => {
        console.log(
          "onaudioprocess called, isRecording:",
          isRecordingRef.current
        );
        if (!isRecordingRef.current) {
          return;
        }

        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);

        // Convert float32 audio data to 16-bit PCM
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          // Clamp the value to 16-bit range and convert
          const sample = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = sample * 0x7fff;
        }

        // Convert PCM data to base64 for WebSocket transmission
        const uint8Array = new Uint8Array(pcmData.buffer);
        const base64Data = btoa(String.fromCharCode(...uint8Array));

        // Call the audio data callback
        if (callbacks.onAudioData) {
          console.log(
            `Sending audio data: sequence ${audioSequenceRef.current}, size: ${base64Data.length} chars`
          );
          callbacks.onAudioData(base64Data, audioSequenceRef.current++);
        }
      };

      console.log("Setting hasPermission to true and isInitialized to true");
      setState((prev) => ({
        ...prev,
        hasPermission: true,
        isInitialized: true,
        permissionError: null,
      }));

      console.log("Audio initialization completed successfully");
      // Start monitoring audio levels
      startAudioLevelMonitoring();
    } catch (err) {
      console.error("Audio initialization error:", err);

      let errorMessage = "Failed to access microphone";

      if (err instanceof Error) {
        switch (err.name) {
          case "NotAllowedError":
            errorMessage =
              "Microphone access denied. Please allow microphone access in your browser settings and try again.";
            break;
          case "NotFoundError":
            errorMessage =
              "No microphone found. Please connect a microphone and try again.";
            break;
          case "NotReadableError":
            errorMessage =
              "Microphone is already in use by another application. Please close other applications using the microphone.";
            break;
          case "OverconstrainedError":
            errorMessage =
              "Microphone does not support the required audio settings. Please try with a different microphone.";
            break;
          case "SecurityError":
            errorMessage =
              "Microphone access blocked due to security restrictions. Please ensure you are using HTTPS.";
            break;
          default:
            errorMessage = `Microphone error: ${err.message}`;
        }
      }

      setState((prev) => ({
        ...prev,
        hasPermission: false,
        isInitialized: false,
        permissionError: errorMessage,
      }));

      if (callbacks.onError) {
        callbacks.onError(errorMessage);
      }
    }
  }, [callbacks, state.isRecording]);

  // Start monitoring audio levels for visualization
  const startAudioLevelMonitoring = useCallback(() => {
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
    }

    audioLevelIntervalRef.current = window.setInterval(() => {
      if (analyserRef.current && state.hasPermission) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate RMS (Root Mean Square) for more accurate audio level
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const normalizedLevel = Math.min(100, (rms / 255) * 100);

        setState((prev) => ({ ...prev, audioLevel: normalizedLevel }));
      }
    }, 50); // Update every 50ms for smooth visualization
  }, [state.hasPermission]);

  // Start recording
  const startRecording = useCallback(() => {
    console.log(
      "startRecording called, hasPermission:",
      state.hasPermission,
      "isInitialized:",
      state.isInitialized
    );
    if (!state.hasPermission || !state.isInitialized) {
      callbacks.onError?.(
        "Microphone not initialized. Please grant permission first."
      );
      return;
    }

    if (audioContextRef.current?.state === "suspended") {
      console.log("Resuming audio context");
      audioContextRef.current.resume();
    }

    console.log("Setting isRecording to true");
    isRecordingRef.current = true;
    setState((prev) => ({ ...prev, isRecording: true }));
    audioSequenceRef.current = 0; // Reset sequence counter
  }, [state.hasPermission, state.isInitialized, callbacks]);

  // Stop recording
  const stopRecording = useCallback(() => {
    console.log("Setting isRecording to false");
    isRecordingRef.current = false;
    setState((prev) => ({ ...prev, isRecording: false }));
  }, []);

  // Toggle recording state
  const toggleRecording = useCallback(() => {
    if (state.isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [state.isRecording, startRecording, stopRecording]);

  // Get current audio level as a percentage
  const getAudioLevel = useCallback(() => {
    return state.audioLevel;
  }, [state.audioLevel]);

  // Check if audio is currently being captured
  const isCapturing = useCallback(() => {
    return state.isRecording && state.hasPermission && state.isInitialized;
  }, [state.isRecording, state.hasPermission, state.isInitialized]);

  // Cleanup function
  const cleanup = useCallback(() => {
    // Stop audio level monitoring
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }

    // Disconnect audio processing nodes
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }

    // Reset state
    isRecordingRef.current = false;
    setState({
      isRecording: false,
      audioLevel: 0,
      hasPermission: false,
      permissionError: null,
      isInitialized: false,
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    // State
    ...state,

    // Actions
    initialize,
    startRecording,
    stopRecording,
    toggleRecording,
    cleanup,

    // Getters
    getAudioLevel,
    isCapturing,
  };
}
